import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    Box, TextField, Button, Typography, Alert, CircularProgress,
    Card, CardContent, Divider, ThemeProvider, createTheme,
    InputAdornment, IconButton
} from '@mui/material';
import {
    FitnessCenter, Lock, Email, ArrowBack,
    PersonAdd, Visibility, VisibilityOff
} from '@mui/icons-material';
import SEO from '../components/SEO'; // ✅ ДОДАДЕНО

const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: '#ff4a97' },
        background: { default: '#fafafa', paper: '#ffffff' },
    }
});

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [emailNotVerified, setEmailNotVerified] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const redirectMessage = location.state?.message;
    const redirectTo = location.state?.redirectTo;

    useEffect(() => {
        document.body.style.backgroundColor = '#fafafa';
        return () => { document.body.style.backgroundColor = ''; };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const result = await login(email, password);
            if (result.success) {
                const user = result.user || JSON.parse(localStorage.getItem('user') || '{}');
                let destination = user.role === 'ADMIN' ? '/admin' : '/user-dashboard';
                if (redirectTo && user.role === 'USER') destination = redirectTo;
                setTimeout(() => navigate(destination, { replace: true }), 500);
            } else {
                if (result.emailNotVerified) setEmailNotVerified(true);
                setError(result.message || 'Грешка при најавување');
            }
        } catch (err) {
            setError('Грешка при поврзување со серверот');
        } finally {
            setLoading(false);
        }
    };

    const homeBtnSx = {
        position: 'absolute', top: 20, left: 20,
        borderRadius: '12px', fontWeight: '800',
        textTransform: 'none', px: 3, py: 1,
        background: 'linear-gradient(45deg, #ff7eb9 30%, #ff4a97 90%)',
        color: '#ffffff', boxShadow: '0 4px 12px rgba(255, 126, 185, 0.4)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(45deg, #ff4a97 30%, #e63985 90%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 15px rgba(255, 126, 185, 0.5)',
        }
    };

    const primaryBtnSx = {
        py: 1.8, borderRadius: '14px', fontWeight: '800',
        fontSize: '1.05rem', textTransform: 'none',
        background: 'linear-gradient(45deg, #ff7eb9 30%, #ff4a97 90%)',
        color: '#fff', boxShadow: '0 4px 15px rgba(255, 126, 185, 0.4)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(45deg, #ff4a97 30%, #e63985 90%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(255, 126, 185, 0.5)',
        }
    };

    const registerBtnSx = {
        py: 1.5, borderRadius: '14px', fontWeight: '700',
        textTransform: 'none', bgcolor: '#1976d2', color: '#ffffff',
        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
            bgcolor: '#115293', transform: 'translateY(-2px)',
            boxShadow: '0 6px 15px rgba(25, 118, 210, 0.4)',
        }
    };

    return (
        <ThemeProvider theme={lightTheme}>

            {/* ✅ SEO */}
            <SEO
                title="Најава - BodyLab by Anamarija"
                description="Најави се во твојот BodyLab профил и пристапи до твоите персонализирани фитнес програми и диети."
                url="/login"
            />

            <Box sx={{
                py: 8, minHeight: '100vh', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                bgcolor: '#fafafa', position: 'relative'
            }}>
                {/* КОПЧЕ ПОЧЕТНА */}
                <Button
                    onClick={() => navigate('/')}
                    startIcon={<ArrowBack sx={{ color: '#fff' }} />}
                    variant="contained"
                    sx={homeBtnSx}
                >
                    Почетна
                </Button>

                <Box sx={{ width: '100%', maxWidth: 500, px: 2 }}>
                    <Card sx={{
                        borderRadius: '24px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.05)',
                        border: '1px solid #ff7eb933'
                    }}>
                        <CardContent sx={{ p: { xs: 3, md: 5 } }}>

                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <FitnessCenter sx={{ fontSize: 50, color: '#ff7eb9', mb: 1 }} />

                                {/* ✅ H1 */}
                                <Typography variant="h1" sx={{
                                    fontWeight: '900', color: '#d63384',
                                    fontSize: { xs: '1.6rem', md: '2rem' }
                                }}>
                                    Добредојде во BodyLab
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#868e96' }}>
                                    Внесете ги вашите податоци за најава
                                </Typography>
                            </Box>

                            {redirectMessage && (
                                <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
                                    {redirectMessage}
                                </Alert>
                            )}
                            {error && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth label="Email адреса"
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    sx={{ mb: 3 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment>,
                                        sx: { borderRadius: '12px' }
                                    }}
                                />

                                <TextField
                                    fullWidth label="Лозинка"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    sx={{ mb: 1 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: '12px' }
                                    }}
                                />

                                <Box sx={{ textAlign: 'right', mb: 4 }}>
                                    <Link to="/forgot-password" style={{
                                        color: '#ff7eb9', textDecoration: 'none',
                                        fontSize: '0.85rem', fontWeight: 'bold'
                                    }}>
                                        Ја заборавивте лозинката?
                                    </Link>
                                </Box>

                                <Button type="submit" fullWidth variant="contained"
                                        disabled={loading} sx={primaryBtnSx}>
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Најави се'}
                                </Button>
                            </form>

                            <Divider sx={{ my: 4 }}>Немаш профил?</Divider>

                            <Button component={Link} to="/register" fullWidth
                                    variant="contained" sx={registerBtnSx} startIcon={<PersonAdd />}>
                                Регистрирај се сега
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default Login;